import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portaleAPI } from '../utils/api';
import {
  ArrowLeftIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/constants';
import toast from 'react-hot-toast';

const STATUS_BADGE: Record<string, string> = {
  bozza: 'bg-gray-100 text-gray-800',
  inviata: 'bg-blue-100 text-blue-800',
  accettata: 'bg-green-100 text-green-800',
  rifiutata: 'bg-red-100 text-red-800',
  scaduta: 'bg-amber-100 text-amber-800',
};

const STATUS_LABEL: Record<string, string> = {
  bozza: 'Bozza',
  inviata: 'Inviata',
  accettata: 'Accettata',
  rifiutata: 'Rifiutata',
  scaduta: 'Scaduta',
};

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['my-order', id],
    queryFn: () => portaleAPI.getOrder(id!),
    enabled: !!id,
  });

  // Get profile to check mostra_prezzi
  const { data: profileData } = useQuery({
    queryKey: ['customer-profile'],
    queryFn: () => portaleAPI.getProfile(),
  });
  const mostraPrezzi = profileData?.data?.mostra_prezzi ?? false;

  const deleteMutation = useMutation({
    mutationFn: () => portaleAPI.deleteOrder(id!),
    onSuccess: () => {
      toast.success('Ordine eliminato');
      queryClient.invalidateQueries({ queryKey: ['my-orders'] });
      navigate('/my-orders');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    },
  });

  const order = data?.data;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse bg-gray-200 h-8 w-64 rounded" />
        <div className="animate-pulse bg-gray-200 h-64 rounded-lg" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ordine non trovato</h3>
          <Link
            to="/my-orders"
            className="text-primary-600 hover:text-primary-800 font-medium"
          >
            Torna ai miei ordini
          </Link>
        </div>
      </div>
    );
  }

  const canEdit = order.stato === 'bozza' && order.origine === 'cliente';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/my-orders')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Ordine {order.numero}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[order.stato] || 'bg-gray-100 text-gray-800'}`}>
                {STATUS_LABEL[order.stato] || order.stato}
              </span>
            </div>
            <p className="text-gray-600 mt-1">{order.titolo}</p>
          </div>
        </div>

        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (confirm('Sei sicuro di voler eliminare questo ordine?')) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
              Elimina
            </button>
          </div>
        )}
      </div>

      {/* Order Info */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Data creazione</span>
            <p className="font-medium text-gray-900">
              {new Date(order.created_at).toLocaleDateString('it-IT', {
                year: 'numeric', month: 'long', day: 'numeric',
              })}
            </p>
          </div>
          {order.scadenza && (
            <div>
              <span className="text-gray-500">Scadenza</span>
              <p className="font-medium text-gray-900">
                {new Date(order.scadenza).toLocaleDateString('it-IT', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}
              </p>
            </div>
          )}
          {mostraPrezzi && (
            <div>
              <span className="text-gray-500">Importo totale</span>
              <p className="font-medium text-gray-900 text-lg">
                {formatCurrency(order.importo_totale)}
              </p>
            </div>
          )}
        </div>
        {order.note && (
          <div className="mt-4 pt-4 border-t">
            <span className="text-sm text-gray-500">Note</span>
            <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{order.note}</p>
          </div>
        )}
      </div>

      {/* Items */}
      {order.items && order.items.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Prodotti</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prodotto
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantità
                </th>
                {mostraPrezzi && (
                  <>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prezzo
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subtotale
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {order.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.descrizione}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">
                    {item.quantita} {item.unita_misura || 'pz'}
                  </td>
                  {mostraPrezzi && (
                    <>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">
                        {formatCurrency(item.prezzo_unitario)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(item.importo_totale)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
            {mostraPrezzi && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                    Totale
                  </td>
                  <td className="px-6 py-4 text-base font-bold text-gray-900 text-right">
                    {formatCurrency(order.importo_totale)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
